/**
 * 백엔드를 부르는 한 자리.
 *
 * ⚠ **한 자리인 것이 요점이다.** 401·pin_required·setup_required·403 을 화면마다
 * 처리하면 언젠가 하나를 빠뜨리고, 빠뜨린 그 화면에서 PIN 만 안 푼 사람이 로그아웃당한다.
 *
 * 부르는 곳은 거래소백엔드 `/api/v1/admin/**` 하나뿐이다(명세 0-3, 앱 사이 약속 0절
 * '앱은 자기 백엔드만 부른다'). 지갑·간편결제·체인은 부르지 않는다.
 */
import { ApiError, type ApiErrorBody } from './errors'
import { getToken } from '@/auth/token'
import { reportAuthFailure } from '@/auth/authEvents'

/**
 * 거래소백엔드 `…/api/v1`. 배포마다 다르다 — 빌드 때 `VITE_API_BASE` 로 굽는다.
 * 값이 없으면 개발 주소(거래소백엔드 기본 포트 8160 — 계약 0-1).
 */
export const API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined)
  || 'http://localhost:8160/api/v1'

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /**
   * 파일을 올리는 본문(multipart/form-data — 사고 문서 XM-115). ⚠ Content-Type 은 붙이지 않는다 — 브라우저가 경계
   * (boundary)와 함께 붙인다. `body` 와 같이 쓰지 않는다.
   */
  form?: FormData
  /** 로그인처럼 아직 토큰이 없는 요청. 이 요청의 401 은 세션 만료가 아니다. */
  anonymous?: boolean
  signal?: AbortSignal
}

function authHeaders(opts: RequestOptions): Record<string, string> {
  const headers: Record<string, string> = {}
  // ⚠ **Content-Type 은 본문이 있을 때만 붙인다.** 본문 없는 GET 에 붙이면
  // 브라우저가 단순 요청으로 보내던 것을 preflight 대상으로 바꾼다.
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (!opts.anonymous) {
    const token = getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  try {
    return await fetch(API_BASE + path, {
      method: opts.method ?? 'GET',
      headers: authHeaders(opts),
      body: opts.form ?? (opts.body === undefined ? null : JSON.stringify(opts.body)),
      // ⚠ **credentials 를 보내지 않는다.** 세션은 Bearer 헤더로 가고, 서버는
      // Access-Control-Allow-Credentials 를 켜지 않았다.
      credentials: 'omit',
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
  } catch {
    throw new ApiError(0, { error: 'network', message: 'Could not connect to the server.' })
  }
}

/**
 * 실패한 답을 오류로 바꾸고, 인증이 무너진 것이면 세션에 알린다.
 *
 * ⚠ **조회만이 아니라 모든 호출이 여기를 지난다.** 처음에는 TanStack 의 캐시 오류
 * 처리기에만 걸어 두었는데, 비밀번호·PIN 변경처럼 조회가 아닌 호출은 그 그물을
 * 빠져나갔다 — 세션이 죽었는데 화면은 「요청을 처리하지 못했습니다」만 띄우고 그대로
 * 서 있었다(레거시 브라우저 시험이 잡았다).
 *
 * ⚠ **`/admin/me` 의 403 은 세션에 다시 알리지 않는다.** PIN·처음 설정·`forbidden` 을
 * 받으면 세션이 XM-06 을 다시 읽는데(명세 2-5), XM-06 자신이 그렇게 답하면 끝없이 돈다.
 * XM-06 에서는 세션 만료(401)만 알린다.
 *
 * ⚠ **익명 요청(로그인)의 401 은 알리지 않는다.** 세션이 죽은 것이 아니다 — 로그인 화면이
 * 스스로 말한다.
 */
function fail(path: string, opts: RequestOptions, status: number, parsed: unknown): ApiError {
  const err = new ApiError(status, (parsed ?? {}) as Partial<ApiErrorBody>)
  if (opts.anonymous) return err
  if (err.kind === 'session_expired') {
    reportAuthFailure(err.kind)
  } else if (path === '/admin/me') {
    return err
  } else if (err.kind === 'pin_required' || err.kind === 'setup_required') {
    reportAuthFailure(err.kind)
  } else if (err.code === 'forbidden') {
    reportAuthFailure('permission_denied')
  }
  return err
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await send(path, opts)
  if (res.status === 204) return undefined as T

  const text = await res.text()
  let parsed: unknown = undefined
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = undefined
    }
  }

  if (!res.ok) throw fail(path, opts, res.status, parsed)
  return parsed as T
}

/**
 * 바이트를 받는 창구(증빙 사진 XM-59). **인증 헤더를 요구**하므로 `<img src>` 로 직접
 * 못 건다 — 받아서 object URL 로 바꿔 준다. 호출자가 `URL.revokeObjectURL` 을 맡는다.
 */
export async function apiBlobUrl(path: string, opts: RequestOptions = {}): Promise<string> {
  const res = await send(path, opts)
  if (!res.ok) {
    let parsed: unknown = undefined
    try { parsed = JSON.parse(await res.text()) } catch { parsed = undefined }
    throw fail(path, opts, res.status, parsed)
  }
  return URL.createObjectURL(await res.blob())
}

/** 쿼리 문자열을 만든다 — 빈 값은 아예 안 보낸다. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v))
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}
